import apiClient from './api';

const API_URL = '/shifts/wizard';

export interface FixedAssignment {
  userId: string;
  userName?: string;
  templateId: string;
  templateName?: string;
  startDate: string;
  endDate: string;
  shiftType: string;
  location: string;
}

export interface WizardData {
  startDate: string;
  endDate: string;
  controller1Id: string;
  controller2Id: string;
  officeStaffIds: string[];
  fixedAssignments: FixedAssignment[];
  heathrowClearerCount: number;
  heathrowAdditionalClearers: number;
  lgwDriverCount: number;
  lgwAdditionalClearers: number;
  officeStartTime: string;
  officeEndTime: string;
  weekendBackupEnabled: boolean;
  weekendBackupUserIds?: string[];
  preferredStartingControllerId?: string;

  // Generation Flags
  generateControllers?: boolean;
  generateFixedAssignments?: boolean;
  generateOfficeStaff?: boolean;
  generateHeathrowClearers?: boolean;
  generateLGWClearers?: boolean;
}

/**
 * Step 1: Validate date range
 */
export const validateDateRange = async (startDate: Date, endDate: Date) => {
  const response = await apiClient.post(`${API_URL}/step1`, {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  return response.data;
};

/**
 * Step 2: Generate controller pattern
 */
export const generateControllerPattern = async (
  startDate: Date,
  endDate: Date,
  controller1Id: string,
  controller2Id: string
) => {
  const response = await apiClient.post(`${API_URL}/step2`, {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    controller1Id,
    controller2Id,
  });
  return response.data;
};

/**
 * Step 3: Generate office pattern
 */
export const generateOfficePattern = async (
  startDate: Date,
  endDate: Date,
  versionId: string,
  officeStaffIds: string[],
  weekendBackupEnabled: boolean,
  weekendTrainingStaffIds?: string[],
  officeStartTime?: string,
  officeEndTime?: string
) => {
  const response = await apiClient.post(`${API_URL}/step3`, {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    versionId,
    officeStaffIds,
    weekendBackupEnabled,
    weekendTrainingStaffIds,
    officeStartTime,
    officeEndTime,
  });
  return response.data;
};

/**
 * Step 4: Generate clearer patterns
 */
export const generateClearerPattern = async (
  startDate: Date,
  endDate: Date,
  versionId: string,
  heathrowClearerCount: number,
  heathrowAdditionalClearers?: number,
  lgwDriverCount?: number,
  lgwAdditionalClearers?: number,
  includeAgency?: boolean,
  clearerStartTime?: string,
  clearerEndTime?: string
) => {
  const response = await apiClient.post(`${API_URL}/step4`, {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    versionId,
    heathrowClearerCount,
    heathrowAdditionalClearers,
    lgwDriverCount,
    lgwAdditionalClearers,
    includeAgency,
    clearerStartTime,
    clearerEndTime,
  });
  return response.data;
};

/**
 * Step 5: Validate coverage
 */
export const validateCoverage = async (versionId: string) => {
  const response = await apiClient.post(`${API_URL}/step5`, {
    versionId,
  });
  return response.data;
};

/**
 * Complete and publish roster
 */
export const completeRoster = async (versionId: string) => {
  const response = await apiClient.post(`/shifts/wizard/complete`, {
    versionId,
  });
  return response.data;
};

/**
 * Preview roster
 */
export const previewRoster = async (versionId: string) => {
  const response = await apiClient.get(`${API_URL}/preview/${versionId}`);
  return response.data;
};

/**
 * Generate complete roster in one call
 */
export const generateCompleteRoster = async (data: {
  startDate: Date;
  endDate: Date;
  controller1Id: string;
  controller2Id: string;
  officeStaffIds?: string[];
  weekendBackupEnabled?: boolean;
  heathrowClearerCount?: number;
  heathrowAdditionalClearers?: number;
  lgwDriverCount?: number;
  lgwAdditionalClearers?: number;
  includeAgency?: boolean;
  officeStartTime?: string;
  officeEndTime?: string;
  clearerStartTime?: string;
  clearerEndTime?: string;
  fixedAssignments?: Array<any>;
  preferredStartingControllerId?: string;
  weekendBackupUserIds?: string[];
  generateControllers?: boolean;
  generateFixedAssignments?: boolean;
  generateOfficeStaff?: boolean;
  generateHeathrowClearers?: boolean;
  generateLGWClearers?: boolean;
}) => {
  const response = await apiClient.post(`${API_URL}/generate`, {
    startDate: data.startDate.toISOString(),
    endDate: data.endDate.toISOString(),
    controller1Id: data.controller1Id,
    controller2Id: data.controller2Id,
    officeStaffIds: data.officeStaffIds || [],
    weekendBackupEnabled: data.weekendBackupEnabled !== false,
    heathrowClearerCount: data.heathrowClearerCount || 0,
    heathrowAdditionalClearers: data.heathrowAdditionalClearers || 0,
    lgwDriverCount: data.lgwDriverCount || 0,
    lgwAdditionalClearers: data.lgwAdditionalClearers || 0,
    includeAgency: data.includeAgency !== false,
    officeStartTime: data.officeStartTime,
    officeEndTime: data.officeEndTime,
    clearerStartTime: data.clearerStartTime,
    clearerEndTime: data.clearerEndTime,
    fixedAssignments: data.fixedAssignments || [],
    preferredStartingControllerId: data.preferredStartingControllerId,
    weekendBackupUserIds: data.weekendBackupUserIds || [],
    generateControllers: data.generateControllers,
    generateFixedAssignments: data.generateFixedAssignments,
    generateOfficeStaff: data.generateOfficeStaff,
    generateHeathrowClearers: data.generateHeathrowClearers,
    generateLGWClearers: data.generateLGWClearers,
  });
  return response.data;
};

/**
 * Get initial data for wizard
 */
export const getInitialData = async () => {
  const response = await apiClient.get(`${API_URL}/initial-data`);
  return response.data;
};

/**
 * Get all staff with conflicts (leave requests, swap requests) for auto-population
 */
export const getStaffWithConflicts = async (startDate: Date, endDate: Date) => {
  const response = await apiClient.get(`${API_URL}/staff-with-conflicts`, {
    params: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  });
  return response.data;
};



// NEW Methods
export const saveRequirements = (spec: any, rosterVersionId: string) => {
  return apiClient.post('/roster-requirements/save', { spec, rosterVersionId });
};

export const autoAllocateRoster = (spec: any, rosterVersionId: string, startDate: string, endDate: string) => {
  return apiClient.post('/roster-requirements/auto-allocate', {
    spec,
    rosterVersionId,
    startDate,
    endDate
  });
};

export const getRequirements = (rosterVersionId: string) => {
  return apiClient.get(`/roster-requirements/${rosterVersionId}`);
};

const rosterWizardService = {
  validateDateRange,
  generateControllerPattern,
  generateOfficePattern,
  generateClearerPattern,
  validateCoverage,
  completeRoster,
  previewRoster,
  generateCompleteRoster,
  getInitialData,
  getStaffWithConflicts,
  saveRequirements,
  autoAllocateRoster,
  getRequirements
};

export default rosterWizardService;
